import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FormFieldMessage } from './FormFieldMessage';
import { ChannelSelector } from '@/components/channels/ChannelSelector';
import { supabase } from '@/integrations/supabase/client';
import { extractContentFromFile } from '@/utils/universalContentExtractor';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Upload, 
  File, 
  X, 
  Loader2, 
  Lightbulb, 
  CheckCircle2, 
  XCircle,
  Plus
} from 'lucide-react';

type FormStep = 'upload' | 'channel' | 'processing' | 'title' | 'description' | 'domains' | 'review';

const STEP_ORDER: FormStep[] = ['upload', 'channel', 'processing', 'title', 'description', 'domains', 'review'];

interface ImportContentInlineFormProps {
  userId: string;
  onComplete: (kbId: string) => void;
  onCancel: () => void;
}

interface AIMetadata {
  title: string;
  titleSuggestion?: string;
  description: string;
  descriptionSuggestion?: string;
  suggestedDomainIds: string[];
  domainConfidence: Record<string, number>;
  overallConfidence: number;
  domainReasonings?: Array<{ id: string; reasoning: string }>;
}

export const ImportContentInlineForm = ({ userId, onComplete, onCancel }: ImportContentInlineFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>('upload');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [selectedChannelName, setSelectedChannelName] = useState<string | null>(null);
  const [extractedContent, setExtractedContent] = useState<string>('');
  const [uploadedFileData, setUploadedFileData] = useState<Array<{name: string; url: string; size: number; type: string}>>([]);
  const [aiMetadata, setAiMetadata] = useState<AIMetadata | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDomainIds, setSelectedDomainIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch channels
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: async () => {
      const { data, error } = await supabase.from('channels').select('*').order('name');
      if (error) throw error;
      return data;
    }
  });

  // Fetch domains for selected channel
  const { data: domains = [] } = useQuery({
    queryKey: ['domains', selectedChannelId],
    queryFn: async () => {
      let query = supabase.from('domains').select('*').order('name');
      if (selectedChannelId) {
        query = query.eq('channel_id', selectedChannelId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: currentStep === 'domains' || currentStep === 'review'
  });

  const getCurrentStepIndex = () => STEP_ORDER.indexOf(currentStep);
  const progress = ((getCurrentStepIndex() + 1) / STEP_ORDER.length) * 100;

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChannelSelect = (channelId: string) => {
    setSelectedChannelId(channelId);
    const channel = channels.find(c => c.id === channelId);
    setSelectedChannelName(channel?.name || null);
  };

  const processFiles = async () => {
    if (selectedFiles.length === 0 || !selectedChannelId) return;

    setCurrentStep('processing');
    setIsProcessing(true);
    setProcessingStatus('Uploading files...');

    try {
      // Upload files
      const uploaded = await Promise.all(
        selectedFiles.map(async (file) => {
          const fileName = `${userId}/${Date.now()}-${file.name}`;
          const { error } = await supabase.storage.from('knowledge-bases').upload(fileName, file);
          if (error) throw error;
          const { data: { publicUrl } } = supabase.storage.from('knowledge-bases').getPublicUrl(fileName);
          return { name: file.name, url: publicUrl, size: file.size, type: file.type };
        })
      );
      setUploadedFileData(uploaded);

      setProcessingStatus('Extracting content...');
      const { content, method } = await extractContentFromFile(selectedFiles[0]);
      setExtractedContent(content);

      if (content.trim().length > 50) {
        setProcessingStatus('Analyzing content with AI...');
        const { data: metadata, error: aiError } = await supabase.functions.invoke('generate-kb-metadata', {
          body: { content, contentType: 'kb', channelId: selectedChannelId }
        });

        if (!aiError && metadata) {
          setAiMetadata({
            title: metadata.title || selectedFiles[0].name.replace(/\.[^/.]+$/, ''),
            titleSuggestion: metadata.titleSuggestion,
            description: metadata.description || '',
            descriptionSuggestion: metadata.descriptionSuggestion,
            suggestedDomainIds: metadata.suggestedDomainIds || [],
            domainConfidence: metadata.domainConfidence || {},
            overallConfidence: metadata.overallConfidence || 0,
            domainReasonings: metadata.domainReasonings || []
          });
          setTitle(metadata.title || selectedFiles[0].name.replace(/\.[^/.]+$/, ''));
          setDescription(metadata.description || '');
          setSelectedDomainIds(metadata.suggestedDomainIds || []);
        } else {
          // Fallback
          const fallbackTitle = selectedFiles[0].name.split('.')[0].replace(/[_-]/g, ' ');
          setTitle(`KB: ${fallbackTitle}`);
          setAiMetadata({
            title: `KB: ${fallbackTitle}`,
            description: `Imported from: ${selectedFiles[0].name}`,
            suggestedDomainIds: [],
            domainConfidence: {},
            overallConfidence: 0.3
          });
        }
      } else {
        const fallbackTitle = selectedFiles[0].name.split('.')[0].replace(/[_-]/g, ' ');
        setTitle(`KB: ${fallbackTitle}`);
        setAiMetadata({
          title: `KB: ${fallbackTitle}`,
          description: `Imported from: ${selectedFiles[0].name}`,
          suggestedDomainIds: [],
          domainConfidence: {},
          overallConfidence: 0.3
        });
      }

      setCurrentStep('title');
    } catch (error) {
      console.error('Processing error:', error);
      toast.error('Failed to process files');
      setCurrentStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleDomain = (domainId: string) => {
    setSelectedDomainIds(prev =>
      prev.includes(domainId)
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const handlePublish = async () => {
    if (!title.trim() || selectedDomainIds.length === 0) {
      toast.error('Please provide a title and select at least one domain');
      return;
    }

    setIsPublishing(true);

    try {
      // Create KB record
      const { data: kb, error: kbError } = await supabase
        .from('knowledge_bases')
        .insert({
          creator_id: userId,
          title: title.trim(),
          description: description.trim() || null,
          status: 'published',
          published_at: new Date().toISOString(),
          file_url: uploadedFileData[0]?.url,
          file_name: uploadedFileData[0]?.name,
          file_type: uploadedFileData[0]?.type,
          file_size: uploadedFileData[0]?.size,
          channel_id: selectedChannelId,
          channel_name: selectedChannelName,
          content: extractedContent.substring(0, 50000), // Store content
        })
        .select()
        .single();

      if (kbError) throw kbError;

      // Auto-populate domains
      if (selectedDomainIds.length > 0) {
        await supabase.rpc('auto_populate_kb_domains', {
          kb_id: kb.id,
          domain_ids: selectedDomainIds
        });
      }

      // Create attribution
      await supabase.from('attributions').insert({
        knowledge_base_id: kb.id,
        user_id: userId,
        ownership_percentage: 100,
        contribution_type: 'creator'
      });

      toast.success('Knowledge Base Published!');
      onComplete(kb.id);
    } catch (error) {
      console.error('Error publishing KB:', error);
      toast.error('Failed to publish knowledge base');
    } finally {
      setIsPublishing(false);
    }
  };

  const goToStep = (step: FormStep) => setCurrentStep(step);

  const canProceed = () => {
    switch (currentStep) {
      case 'upload': return selectedFiles.length > 0;
      case 'channel': return !!selectedChannelId;
      case 'title': return title.trim().length > 0;
      case 'description': return true;
      case 'domains': return selectedDomainIds.length > 0;
      case 'review': return true;
      default: return false;
    }
  };

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Progress Bar */}
      <FormFieldMessage variant="assistant">
        <div className="flex items-center gap-3 mb-2">
          <div className="text-sm font-medium">
            Step {getCurrentStepIndex() + 1} of {STEP_ORDER.length}
          </div>
          <Progress value={progress} className="flex-1 h-2" />
        </div>
        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
          <Upload className="h-4 w-4" />
          Import Content
        </div>
      </FormFieldMessage>

      {/* Step: Upload */}
      {currentStep === 'upload' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block">What files would you like to import?</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            {selectedFiles.length === 0 ? (
              <>
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  PDF, Word, PowerPoint, Excel, Text, Audio, Images, and more
                </p>
              </>
            ) : (
              <div className="max-h-40 overflow-y-auto mb-4">
                <ul className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded border">
                      <div className="flex items-center">
                        <File className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-sm truncate max-w-xs">{file.name}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeFile(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Button onClick={() => fileInputRef.current?.click()}>
              {selectedFiles.length > 0 ? 'Add More Files' : 'Browse Files'}
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelection}
            />
          </div>
        </FormFieldMessage>
      )}

      {/* Step: Channel Selection */}
      {currentStep === 'channel' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block">Which channel should this KB belong to?</Label>
          <ChannelSelector
            channels={channels}
            value={selectedChannelId || ''}
            onValueChange={handleChannelSelect}
            placeholder="Choose a channel"
            className="w-full"
          />
          <p className="text-sm text-muted-foreground mt-2">
            This determines which domain categories will be available
          </p>
        </FormFieldMessage>
      )}

      {/* Step: Processing */}
      {currentStep === 'processing' && (
        <FormFieldMessage variant="assistant">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <div>
              <Label className="text-base font-semibold block">{processingStatus}</Label>
              <p className="text-sm text-muted-foreground">This may take a moment...</p>
            </div>
          </div>
        </FormFieldMessage>
      )}

      {/* Step: Title */}
      {currentStep === 'title' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block">What's the title for this Knowledge Base?</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a descriptive title"
            className="mb-2"
          />
          {aiMetadata?.titleSuggestion && title !== aiMetadata.titleSuggestion && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTitle(aiMetadata.titleSuggestion!)}
              className="w-full justify-start text-left"
            >
              <Lightbulb className="mr-2" size={16} />
              <span className="flex-1 truncate">
                AI suggests: <span className="font-semibold">{aiMetadata.titleSuggestion}</span>
              </span>
            </Button>
          )}
        </FormFieldMessage>
      )}

      {/* Step: Description */}
      {currentStep === 'description' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block">Add a description (optional)</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this knowledge base contains..."
            rows={4}
            className="mb-2"
          />
          {aiMetadata?.descriptionSuggestion && description !== aiMetadata.descriptionSuggestion && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDescription(aiMetadata.descriptionSuggestion!)}
              className="w-full justify-start text-left"
            >
              <Lightbulb className="mr-2" size={16} />
              <span className="flex-1 line-clamp-2">
                AI suggests: <span className="font-semibold">{aiMetadata.descriptionSuggestion}</span>
              </span>
            </Button>
          )}
        </FormFieldMessage>
      )}

      {/* Step: Domains */}
      {currentStep === 'domains' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block">Select knowledge domains</Label>
          
          {selectedDomainIds.length > 0 && (
            <div className="mb-3 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                Selected ({selectedDomainIds.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedDomainIds.map(domainId => {
                  const domain = domains.find(d => d.id === domainId);
                  return domain ? (
                    <Badge
                      key={domainId}
                      className="bg-green-600 text-white cursor-pointer hover:bg-green-700"
                      onClick={() => toggleDomain(domainId)}
                    >
                      {domain.name}
                      <XCircle className="h-3 w-3 ml-1" />
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {domains.map(domain => {
              const isSelected = selectedDomainIds.includes(domain.id);
              const confidence = aiMetadata?.domainConfidence[domain.id];
              const isAISuggested = aiMetadata?.suggestedDomainIds.includes(domain.id);

              return (
                <Badge
                  key={domain.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer transition-all hover:scale-105"
                  onClick={() => toggleDomain(domain.id)}
                >
                  {isSelected ? <CheckCircle2 className="mr-1" size={14} /> : <Plus className="mr-1" size={14} />}
                  {domain.name}
                  {isAISuggested && confidence && (
                    <span className="ml-1 text-xs opacity-75">
                      {Math.round(confidence * 100)}%
                    </span>
                  )}
                </Badge>
              );
            })}
          </div>
        </FormFieldMessage>
      )}

      {/* Step: Review */}
      {currentStep === 'review' && (
        <FormFieldMessage variant="assistant">
          <Label className="text-base font-semibold mb-3 block flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Review Your Knowledge Base
          </Label>
          <div className="p-4 space-y-3 bg-muted/30 rounded-lg border">
            <div>
              <p className="text-xs text-muted-foreground">Title</p>
              <p className="font-medium">{title}</p>
            </div>
            {description && (
              <div>
                <p className="text-xs text-muted-foreground">Description</p>
                <p className="text-sm">{description}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">Channel</p>
              <p className="font-medium">{selectedChannelName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Domains</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedDomainIds.map(id => {
                  const domain = domains.find(d => d.id === id);
                  return domain ? (
                    <Badge key={id} variant="secondary" className="text-xs">{domain.name}</Badge>
                  ) : null;
                })}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Files</p>
              <p className="text-sm">{selectedFiles.map(f => f.name).join(', ')}</p>
            </div>
          </div>
        </FormFieldMessage>
      )}

      {/* Navigation Buttons */}
      <FormFieldMessage variant="user">
        <div className="flex justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (currentStep === 'upload') {
                onCancel();
              } else {
                const idx = getCurrentStepIndex();
                if (idx > 0) {
                  // Skip processing step when going back
                  const prevIdx = currentStep === 'title' ? 1 : idx - 1;
                  setCurrentStep(STEP_ORDER[prevIdx]);
                }
              }
            }}
            size="sm"
            disabled={isProcessing}
          >
            {currentStep === 'upload' ? 'Cancel' : 'Back'}
          </Button>

          {currentStep === 'review' ? (
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
              size="sm"
              className="bg-green-700 hover:bg-green-800"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                'Publish Knowledge Base'
              )}
            </Button>
          ) : currentStep === 'channel' ? (
            <Button
              onClick={processFiles}
              disabled={!canProceed() || isProcessing}
              size="sm"
            >
              {isProcessing ? 'Processing...' : 'Continue'}
            </Button>
          ) : currentStep === 'processing' ? null : (
            <Button
              onClick={() => {
                const idx = getCurrentStepIndex();
                if (idx < STEP_ORDER.length - 1) {
                  setCurrentStep(STEP_ORDER[idx + 1]);
                }
              }}
              disabled={!canProceed()}
              size="sm"
            >
              Continue
            </Button>
          )}
        </div>
      </FormFieldMessage>
    </div>
  );
};
